import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  ArrowUp, ArrowDown, Minus, Search, Loader2, Globe, Users, Info,
  ArrowLeft, Activity,
} from "lucide-react";

/*  Phase 1 Global Country Leaderboard.
    Ranks countries strictly by eligible detective reservations.
    Verified-team count is a separate informational column (not scored).
    7-day delta is derived from historical rows — shows "—" if no history.
    No fabricated data: every empty state is a real empty state.
    Pairs with /leaderboard/how-it-works for the public explainer.
*/

const PAGE_SIZE = 50;

const countryToFlag = (code) => {
  const c = (code || "").toUpperCase();
  if (c.length !== 2 || !/^[A-Z]{2}$/.test(c)) return "🌐";
  return String.fromCodePoint(
    0x1F1E6 + c.charCodeAt(0) - 65,
    0x1F1E6 + c.charCodeAt(1) - 65,
  );
};

const Delta = ({ v }) => {
  if (v === null || v === undefined) {
    return (
      <span className="inline-flex items-center text-[#5a5050]" title="No 7-day history yet">
        <Minus size={14} /> <span className="ml-1 text-xs">—</span>
      </span>
    );
  }
  if (v > 0) {
    return (
      <span className="inline-flex items-center text-[#4ade80]" title={`Up ${v} place${v === 1 ? "" : "s"} in 7d`}>
        <ArrowUp size={14} /> <span className="ml-1 text-xs font-mono">{v}</span>
      </span>
    );
  }
  if (v < 0) {
    return (
      <span className="inline-flex items-center text-[#f87171]" title={`Down ${-v} place${-v === 1 ? "" : "s"} in 7d`}>
        <ArrowDown size={14} /> <span className="ml-1 text-xs font-mono">{-v}</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center text-[#a39797]" title="Unchanged in 7d">
      <Minus size={14} /> <span className="ml-1 text-xs">—</span>
    </span>
  );
};

const timeAgo = (iso) => {
  if (!iso) return "";
  try {
    const t = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - t);
    if (diff < 60_000) return "just now";
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    return `${Math.floor(diff / 86_400_000)}d ago`;
  } catch { return ""; }
};

const Ticker = () => {
  const [rows, setRows] = useState(null);  // null = loading, [] = empty state
  const [err, setErr] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/nye/ticker?limit=10");
      setRows(r.data.rows || []);
      setErr(false);
    } catch {
      setErr(true);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);   // every 30s — polite, cheap
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="card-noir p-4 mb-6" data-testid="leaderboard-ticker">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity size={14} className="text-[#d4af37] animate-pulse" />
          <div className="text-[10px] tracking-[0.3em] text-[#d4af37]">WHO'S CLIMBING?</div>
        </div>
        <div className="text-[10px] text-[#5a5050] tracking-widest">LIVE · LAST 10</div>
      </div>
      {rows === null && (
        <div className="flex items-center justify-center py-6 text-[#5a5050] text-sm">
          <Loader2 size={14} className="animate-spin mr-2" /> Reading the wire…
        </div>
      )}
      {err && (
        <div className="text-sm text-[#f87171]" data-testid="ticker-error">
          The wire is quiet right now. We'll retry automatically.
        </div>
      )}
      {rows !== null && !err && rows.length === 0 && (
        <div className="text-center py-6 text-[#a39797] text-sm italic" data-testid="ticker-empty">
          The world is gathering… Be among the first detectives to join.
        </div>
      )}
      {rows !== null && rows.length > 0 && (
        <ul className="flex flex-wrap gap-2" data-testid="ticker-list">
          {rows.map((r, i) => (
            <li key={i}
              className="flex items-center gap-2 bg-[#0a0908]/60 border border-[#2d2424] rounded-full px-3 py-1.5 text-xs"
              data-testid={`ticker-row-${i}`}>
              <span className="text-lg leading-none" aria-hidden="true">
                {r.country ? countryToFlag(r.country) : "🌐"}
              </span>
              <span className="text-[#f4f0ec] font-bold" translate="no">
                {r.country || "???"}
              </span>
              <span className="text-[#5a5050]">·</span>
              <span className="text-[#a39797]">{timeAgo(r.created_at)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Leaderboard = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalAll, setTotalAll] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  const debounceRef = useRef(null);

  const fetchPage = useCallback(async (nextOffset, query, append = false) => {
    setErr("");
    try {
      if (append) setLoadingMore(true); else setLoading(true);
      const params = new URLSearchParams({
        offset: String(nextOffset),
        limit:  String(PAGE_SIZE),
      });
      if (query) params.set("q", query);
      const r = await api.get(`/nye/leaderboard-full?${params.toString()}`);
      const data = r.data || {};
      setRows((prev) => (append ? [...prev, ...(data.rows || [])] : (data.rows || [])));
      setTotal(data.total || 0);
      setTotalAll(data.total_all || 0);
      setHasMore(!!data.has_more);
      setOffset(nextOffset + (data.rows?.length || 0));
    } catch (e) {
      setErr("Couldn't load the leaderboard. Please try again.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // Initial load
  useEffect(() => { fetchPage(0, ""); }, [fetchPage]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchPage(0, q.trim().toUpperCase());
    }, 250);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  return (
    <div className="min-h-screen bg-[#050403] text-[#f4f0ec] pb-20"
      data-testid="leaderboard-page">
      {/* Top bar */}
      <div className="border-b border-[#2d2424] bg-[#0a0908]">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" className="text-xs text-[#a39797] hover:text-[#f4f0ec] flex items-center gap-1.5"
            data-testid="leaderboard-back">
            <ArrowLeft size={14} /> Back
          </Link>
          <div className="text-[10px] tracking-[0.3em] text-[#d4af37]">
            THE WORLD IS WATCHING
          </div>
          <Link to="/leaderboard/how-it-works"
            className="text-xs text-[#a39797] hover:text-[#d4af37] flex items-center gap-1.5"
            data-testid="leaderboard-explainer-link">
            <Info size={14} /> How it works
          </Link>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 pt-8">
        <h1 className="font-display text-4xl sm:text-5xl text-[#f4f0ec] leading-tight">
          Global Country Leaderboard
        </h1>
        <p className="text-sm text-[#a39797] mt-2 mb-6">
          Countries ranked by detectives reserved for the <span className="text-[#d4af37]">Global First Run</span>.
          <br className="hidden sm:block" />
          <span className="text-[#5a5050]">Dec 31, 2026 · 20:00 UTC · Free · Worldwide</span>
        </p>

        <Ticker />

        {/* Summary strip */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
          <div className="card-noir p-4" data-testid="lb-stat-total-countries">
            <div className="text-[10px] tracking-widest text-[#a39797]">COUNTRIES</div>
            <div className="font-display text-3xl text-[#d4af37] mt-1 flex items-center gap-2">
              <Globe size={20} /> {totalAll}
            </div>
          </div>
          <div className="card-noir p-4" data-testid="lb-stat-total-detectives">
            <div className="text-[10px] tracking-widest text-[#a39797]">DETECTIVES WORLDWIDE</div>
            <div className="font-display text-3xl text-[#4ade80] mt-1 flex items-center gap-2">
              <Users size={20} /> {rows.reduce((s, r) => s + (r.detectives || 0), 0).toLocaleString()}
              {hasMore && <span className="text-xs text-[#5a5050]">+</span>}
            </div>
          </div>
          <div className="card-noir p-4 col-span-2 md:col-span-1">
            <div className="text-[10px] tracking-widest text-[#a39797]">SCORING</div>
            <div className="text-sm text-[#f4f0ec] mt-1">
              1 detective = 1 point.
              <Link to="/leaderboard/how-it-works"
                className="text-[#d4af37] hover:underline ml-1"
                data-testid="lb-scoring-link">
                Learn how →
              </Link>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5a5050]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by country code (e.g. US, GB, JP)"
            className="w-full bg-[#0a0908] border border-[#2d2424] rounded-md pl-9 pr-3 py-2.5 text-sm text-[#f4f0ec] focus:outline-none focus:border-[#d4af37]"
            data-testid="lb-search" />
        </div>

        {/* Table */}
        <div className="card-noir overflow-hidden" data-testid="lb-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead>
                <tr className="text-[10px] tracking-widest text-[#a39797] border-b border-[#2d2424] bg-[#0a0908]">
                  <th className="text-left py-3 px-4">RANK</th>
                  <th className="text-left py-3 px-2">COUNTRY</th>
                  <th className="text-right py-3 px-2">DETECTIVES</th>
                  <th className="text-right py-3 px-2 whitespace-nowrap">VERIFIED TEAMS</th>
                  <th className="text-right py-3 px-4 whitespace-nowrap">7-DAY</th>
                </tr>
              </thead>
              <tbody>
                {loading && rows.length === 0 && (
                  <tr><td colSpan={5} className="py-10 text-center text-[#5a5050]">
                    <Loader2 size={16} className="inline animate-spin mr-2" />
                    Loading leaderboard…
                  </td></tr>
                )}
                {!loading && rows.length === 0 && !q && (
                  <tr><td colSpan={5} className="py-10 text-center text-[#a39797] text-sm italic"
                      data-testid="lb-empty-global">
                    The world is gathering… no detectives have reserved yet. Be the first.
                  </td></tr>
                )}
                {!loading && rows.length === 0 && q && (
                  <tr><td colSpan={5} className="py-10 text-center text-[#a39797] text-sm italic"
                      data-testid="lb-empty-search">
                    No countries matched "{q}".
                  </td></tr>
                )}
                {rows.map((r) => (
                  <tr key={r.country} className="border-b border-[#2d2424]/50 hover:bg-[#0a0908]"
                    data-testid={`lb-row-${r.country}`}>
                    <td className="py-3 px-4 font-mono text-[#d4af37]">#{r.rank}</td>
                    <td className="py-3 px-2">
                      <span className="flex items-center gap-3">
                        <span className="text-2xl leading-none" aria-hidden="true">
                          {countryToFlag(r.country)}
                        </span>
                        <span className="text-[#f4f0ec] font-bold tracking-wider" translate="no">
                          {r.country}
                        </span>
                      </span>
                    </td>
                    <td className="py-3 px-2 text-right font-mono text-[#f4f0ec]">
                      {r.detectives.toLocaleString()}
                    </td>
                    <td className="py-3 px-2 text-right font-mono text-[#5a5050]"
                        title="Phase 1: informational only; not scored">
                      {r.verified_teams || 0}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <Delta v={r.delta_7d} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {err && (
          <div className="text-sm text-[#f87171] mt-4" data-testid="lb-error">{err}</div>
        )}

        {hasMore && (
          <div className="text-center mt-5">
            <button
              onClick={() => fetchPage(offset, q.trim().toUpperCase(), true)}
              disabled={loadingMore}
              className="btn-ghost text-xs disabled:opacity-50"
              data-testid="lb-load-more">
              {loadingMore
                ? <><Loader2 size={12} className="animate-spin mr-1" /> Loading…</>
                : <>Load more ({total - rows.length} remaining)</>}
            </button>
          </div>
        )}

        {total > 0 && !hasMore && (
          <div className="text-center text-xs text-[#5a5050] mt-5">
            Showing all {total.toLocaleString()} {total === 1 ? "country" : "countries"} ·
            Updated {timeAgo(new Date().toISOString())}
          </div>
        )}
      </div>
    </div>
  );
};

export default Leaderboard;
